// Cards for the meeting and deal decisions: 8.D "Did the meeting happen?", 8.4 "Paste the notes", 9.D "Decision?".
// Each writes through one act action (confirm_attendance, submit_transcript, decide_deal); the database checks the
// person is a member of the business and closes the task.
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { AttendanceOutcome, DealOutcome, HumanTask, MeetingDetail, Stage } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { fmtDateTime, humanise, stageLabel } from "../../lib/format";
import { Async, Empty, Notice, StatusChip } from "../../components/ui";
import { EntityPlaybook } from "../../components/FlavourChip";

interface CardProps {
  crm: Crm;
  task: HumanTask;
  onDone: () => void;
}

/** Runs one save: busy state, the server's refusal shown in place. */
function useSave() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function who(m: MeetingDetail): string {
  const person = m.person?.full_name ?? "the contact";
  return m.organisation?.name ? `${person} at ${m.organisation.name}` : person;
}

// 8.D ------------------------------------------------------------------------------------------
export const ATTENDANCE: { value: AttendanceOutcome; label: string; hint: string }[] = [
  { value: "held", label: "Held", hint: "The firm moves to Met. Your notes go to the deal agent for the summary, next steps and recap" },
  { value: "no_show", label: "No-show", hint: "The scheduler offers two new times (after two no-shows the firm goes to nurture)" },
  { value: "rescheduled", label: "Rescheduled", hint: "Moved by agreement: the scheduler offers two other times; it isn't counted as a no-show" },
];

/** The 8.D answer, on its own so the Meetings page can show it too. */
export function AttendanceForm({ crm, meeting, onDone }: { crm: Crm; meeting: MeetingDetail; onDone: (message: string) => void }) {
  const [outcome, setOutcome] = useState<AttendanceOutcome | "">("");
  const [notes, setNotes] = useState("");
  const [link, setLink] = useState("");
  const { busy, error, run } = useSave();
  const id = useId();
  return (
    <div className="attendance-form">
      <fieldset className="radios">
        <legend>Did the meeting with {who(meeting)} happen?</legend>
        {ATTENDANCE.map((o) => (
          <label key={o.value} className="radio">
            <input type="radio" name={`${id}-outcome`} value={o.value} checked={outcome === o.value} onChange={() => setOutcome(o.value)} />
            <span className="radio-text">
              <span>{o.label}</span>
              <span className="muted small">{o.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {outcome && (
        <label className="field" htmlFor={`${id}-notes`}>
          <span className="field-label">{outcome === "held" ? "Notes or the transcript (optional)" : "Note (optional)"}</span>
          <textarea id={`${id}-notes`} rows={outcome === "held" ? 6 : 2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          {outcome === "held" && (
            <span className="muted small">
              Paste what was agreed, or the whole transcript. It is used for the summary and next steps, then not kept. You can also add it later on the Meetings page.
            </span>
          )}
        </label>
      )}
      {outcome === "held" && (
        <label className="field" htmlFor={`${id}-link`}>
          <span className="field-label">Transcript link (optional, https)</span>
          <input id={`${id}-link`} type="url" inputMode="url" placeholder="https://notes.wisprflow.ai/shared/…" value={link} onChange={(e) => setLink(e.target.value)} />
        </label>
      )}
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!outcome || busy}
          onClick={() =>
            outcome &&
            void run(async () => {
              const r = await crm.confirmAttendance(meeting.id, outcome, { notes, transcriptUrl: link });
              onDone(
                r?.ignored
                  ? `That meeting was already marked ${humanise(String(r.status)).toLowerCase()}.`
                  : outcome === "held"
                    ? notes.trim() || link.trim()
                      ? "Marked as held. The deal agent is writing the summary and next steps."
                      : "Marked as held. Add your notes on the Meetings page when you have them."
                    : outcome === "no_show"
                      ? "Marked as a no-show. The scheduler will offer two new times."
                      : "Marked as rescheduled. The scheduler will offer two other times.",
              );
            })
          }
        >
          {busy ? "Saving…" : outcome ? `Save: ${ATTENDANCE.find((o) => o.value === outcome)!.label.toLowerCase()}` : "Choose what happened"}
        </button>
      </div>
    </div>
  );
}

/**
 * 8.1, 8.3 A meeting coming up (hold_meeting, due when it starts; read_brief, due two hours before): the time, Join,
 * and the meeting card with the brief. Done closes the task.
 */
export function MeetingSoonTask({ crm, task, onDone }: CardProps) {
  const meetingId = String(task.payload.meeting_id ?? task.entity_id ?? "");
  const m = useAsync(() => crm.meeting(meetingId), [crm, meetingId]);
  const { busy, error, run } = useSave();
  return (
    <Async state={m} empty={(x) => (!x ? <Empty title="This meeting no longer exists" /> : null)}>
      {(x) => (
        <div>
          <p className="card-lede">
            {fmtDateTime(x!.starts_at)} with <strong>{who(x!)}</strong>. <StatusChip status={x!.status} />
          </p>
          <p className="inline wrap">
            {x!.location && /^https?:\/\//.test(x!.location) && (
              <a className="btn btn-primary btn-small" href={x!.location} target="_blank" rel="noreferrer noopener">
                Join
              </a>
            )}
            <Link className="btn btn-small" to={`/conversations/meetings/${x!.id}`}>
              {task.kind === "read_brief" ? "Read the brief" : "Open the meeting"}
            </Link>
          </p>
          <Notice text={error} tone="bad" />
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void run(async () => (await crm.completeTask(task.id, "done", {}), onDone()))}>
            {busy ? "Saving…" : "Done"}
          </button>
        </div>
      )}
    </Async>
  );
}

/** 9.D No answer after the email following the call and its chasers (engine/path_nudges.py): the owner decides.
 * Closing stays a person's: "Close it as lost" goes to the firm's CRM card, where the deal is closed. */
export function ChasersDecideTask({ crm, task, onDone }: CardProps) {
  const { busy, error, run } = useSave();
  const org = String(task.payload.organisation_id ?? task.entity_id ?? "");
  const decide = (choice: "call" | "wait" | "close") => run(async () => (await crm.completeTask(task.id, "done", { choice }), onDone()));
  return (
    <div>
      <p className="card-lede">{String(task.payload.how ?? "No answer after the email following your call and its chasers.")}</p>
      <p className="inline wrap">
        <button type="button" className="btn btn-small" disabled={busy} onClick={() => void decide("call")}>
          I'll call them
        </button>
        <button type="button" className="btn btn-small" disabled={busy} onClick={() => void decide("wait")}>
          Keep waiting
        </button>
        {org && (
          <Link className="btn btn-quiet btn-small" to={`/contacts/companies/${org}`} onClick={() => void decide("close")}>
            Close it as lost
          </Link>
        )}
      </p>
      <Notice text={error} tone="bad" />
    </div>
  );
}

export function AttendanceTask({ crm, task, onDone }: CardProps) {
  const meetingId = String(task.payload.meeting_id ?? task.entity_id ?? "");
  const m = useAsync(() => crm.meeting(meetingId), [crm, meetingId]);
  const { busy, error, run } = useSave();
  return (
    <Async state={m} empty={(x) => (!x ? <Empty title="This meeting no longer exists" /> : null)}>
      {(x) =>
        x!.status === "booked" ? (
          <div>
            <p className="card-lede">
              {fmtDateTime(x!.starts_at)} with <strong>{who(x!)}</strong>.{" "}
              {x!.organisation_id && <Link to={`/contacts/companies/${x!.organisation_id}`}>Open the firm</Link>}
            </p>
            <AttendanceForm crm={crm} meeting={x!} onDone={onDone} />
          </div>
        ) : (
          <div>
            <p>
              This meeting is already marked <StatusChip status={x!.status} />. Nothing else to do here.
            </p>
            <Notice text={error} tone="bad" />
            <button type="button" className="btn" disabled={busy} onClick={() => void run(async () => (await crm.completeTask(task.id, "done", { already: x!.status }), onDone()))}>
              {busy ? "Saving…" : "Close this task"}
            </button>
          </div>
        )
      }
    </Async>
  );
}

// 8.4 ------------------------------------------------------------------------------------------
/** Paste the notes or the transcript of a held meeting. */
export function TranscriptForm({ crm, meetingId, onDone }: { crm: Crm; meetingId: string; onDone: (message: string) => void }) {
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const { busy, error, run } = useSave();
  const id = useId();
  return (
    <div className="transcript-form">
      <label className="field" htmlFor={`${id}-text`}>
        <span className="field-label">Notes or the transcript</span>
        <textarea id={`${id}-text`} rows={8} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <label className="field" htmlFor={`${id}-link`}>
        <span className="field-label">Or its link (https, optional)</span>
        <input id={`${id}-link`} type="url" inputMode="url" value={link} onChange={(e) => setLink(e.target.value)} />
      </label>
      <p className="muted small">The deal agent writes a short summary on the firm, the next steps and a recap email for you to approve. The text itself is not kept.</p>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || (!text.trim() && !link.trim())}
          onClick={() => void run(async () => (await crm.submitTranscript(meetingId, text, link), onDone("Sent to the deal agent. The summary and next steps follow shortly.")))}
        >
          {busy ? "Saving…" : "Send to the deal agent"}
        </button>
      </div>
    </div>
  );
}

export function MeetingNotesTask({ crm, task, onDone }: CardProps) {
  const p = task.payload;
  const meetingId = String(p.meeting_id ?? task.entity_id ?? "");
  return (
    <div>
      <p className="card-lede">
        The meeting{p.with ? ` with ${String(p.with)}` : ""}
        {p.organisation ? ` at ${String(p.organisation)}` : ""}
        {p.starts_at ? ` on ${fmtDateTime(String(p.starts_at))}` : ""} was held, but there are no notes yet.
      </p>
      {typeof p.how === "string" && <p className="muted small">{p.how}</p>}
      <TranscriptForm crm={crm} meetingId={meetingId} onDone={onDone} />
    </div>
  );
}

// 9.D ------------------------------------------------------------------------------------------
export const DECISIONS: { value: DealOutcome; label: string; hint: string }[] = [
  { value: "proceed", label: "Proceed", hint: "Move the deal to its next stage (or a later one)" },
  { value: "lost", label: "Lost", hint: "Close the deal and say why. If your playbook has a save offer, the engine makes it first" },
  { value: "hold", label: "Hold", hint: "Keep it as it is and ask again on a date" },
];

/** Stages a deal may proceed to: later than its own, never the lost stage. */
export function laterStages(stages: Stage[], current: string): Stage[] {
  const pos = stages.find((s) => s.key === current)?.position ?? -1;
  return stages.filter((s) => s.position > pos && s.key !== "lost");
}

function inDays(n: number): string {
  return new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
}

export function DealDecisionTask({ crm, task, onDone }: CardProps) {
  const dealId = String(task.payload.deal_id ?? task.entity_id ?? "");
  const data = useAsync(async () => {
    const [deal, stages, docs] = await Promise.all([crm.deal(dealId), crm.stages("deal"), crm.dealDocuments(dealId)]);
    return { deal, stages, docs };
  }, [crm, dealId]);
  const [outcome, setOutcome] = useState<DealOutcome | "">("");
  const [stage, setStage] = useState("");
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState(inDays(30));
  const { busy, error, run } = useSave();
  const id = useId();
  const docId = typeof task.payload.document_id === "string" ? task.payload.document_id : null;
  return (
    <Async state={data} empty={(d) => (!d.deal ? <Empty title="This deal no longer exists" /> : null)}>
      {({ deal, stages, docs }) => {
        const d = deal!;
        const next = laterStages(stages, d.stage);
        const doc = docs.find((x) => x.id === docId) ?? null;
        const closed = stages.find((s) => s.key === d.stage)?.is_terminal;
        return (
          <div>
            <p className="card-lede">
              <strong>{d.organisation?.name ?? "This deal"}</strong> is at <strong>{stageLabel(stages.find((s) => s.key === d.stage), d.stage)}</strong>
              {doc ? (
                <>
                  ; the {humanise(doc.kind).toLowerCase()} “{doc.title}” is <StatusChip status={doc.status} />
                </>
              ) : null}
              . {task.payload.held ? "You put it on hold until now." : task.payload.save_offer ? "We made them a save offer: did they take it?" : typeof task.payload.chases === "number" ? `We have chased ${task.payload.chases} times without an answer.` : ""}
            </p>
            <EntityPlaybook crm={crm} entityType="deal" entityId={dealId} />
            {typeof task.payload.save_offer_failed === "string" && <p className="muted small">No save offer could be made: {task.payload.save_offer_failed}.</p>}
            {closed ? (
              <>
                <p className="muted">This deal is already closed, so there is nothing to decide.</p>
                <Notice text={error} tone="bad" />
                <button type="button" className="btn" disabled={busy} onClick={() => void run(async () => (await crm.completeTask(task.id, "done", { already: d.stage }), onDone()))}>
                  {busy ? "Saving…" : "Close this task"}
                </button>
              </>
            ) : (
              <>
                <fieldset className="radios">
                  <legend>What next?</legend>
                  {DECISIONS.map((o) => (
                    <label key={o.value} className="radio">
                      <input type="radio" name={`${id}-o`} value={o.value} checked={outcome === o.value} onChange={() => setOutcome(o.value)} disabled={o.value === "proceed" && next.length === 0} />
                      <span className="radio-text">
                        <span>{o.label}</span>
                        <span className="muted small">{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                {outcome === "proceed" && (
                  <label className="field field-narrow" htmlFor={`${id}-stage`}>
                    <span className="field-label">Move to</span>
                    <select id={`${id}-stage`} value={stage || next[0]?.key || ""} onChange={(e) => setStage(e.target.value)}>
                      {next.map((s) => (
                        <option key={s.key} value={s.key}>
                          {stageLabel(s, s.key)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {outcome === "hold" && (
                  <label className="field field-narrow" htmlFor={`${id}-until`}>
                    <span className="field-label">Ask me again on</span>
                    <input id={`${id}-until`} type="date" min={inDays(1)} max={inDays(365)} value={until} onChange={(e) => setUntil(e.target.value)} />
                  </label>
                )}
                {outcome && (
                  <label className="field" htmlFor={`${id}-reason`}>
                    <span className="field-label">{outcome === "lost" ? "Why was it lost? (needed)" : "Why (optional)"}</span>
                    <input id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </label>
                )}
                <Notice text={error} tone="bad" />
                <div className="action-bar">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!outcome || busy || (outcome === "lost" && !reason.trim())}
                    onClick={() =>
                      outcome &&
                      void run(async () => {
                        await crm.decideDeal(d.id, outcome, { reason, stage: outcome === "proceed" ? stage || next[0]?.key : undefined, holdUntil: outcome === "hold" ? until : undefined });
                        onDone();
                      })
                    }
                  >
                    {busy ? "Saving…" : outcome ? `Save: ${DECISIONS.find((o) => o.value === outcome)!.label.toLowerCase()}` : "Choose what next"}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }}
    </Async>
  );
}
